import { createElement, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import key from 'weak-key';

// styles
import styles from './online-catalog-assets.module.scss';

// types
import type { OkvTileProps } from './online-catalog-assets.types';

// components
import { Image } from 'components/image/image';
import { Translation } from 'components/Translation/Translation';
import { ImageThumbnail } from 'components/image/image-thumbnail';

// utils
import { getImageAltSuffix, imageAlt } from 'components/Product/ProductImageAlt';
import { getPrimaryImage } from 'components/Product/DetailGallery/SelectPrimaryImage';
import { useIsMobile } from 'components/App/SizeProvider';
import { responsiveImage } from 'utils/responsiveImage';
import { sortProductImages } from 'utils/sortProductImages';
import { currentCatalogSelector } from 'utils/selectors/productCatalogSelectors';
import { catalogBaseUrlSelector } from 'utils/selectors/globalsSelectors';
import { useTranslationFunction } from 'utils/hooks/use-translations';
import { useXy } from 'utils/hooks/use-xy';
import { useGroup } from 'utils/hooks/use-group';
import { classNameBuilder } from 'utils/classNameBuilder';

const OkvTile = (props: OkvTileProps) => {
  const { identifier, total, type, product, isAccordion, backgroundColor } = props;
  const { name, subtitle, description, pictureObject, state } = props.product || {};
  const [productLink, setProductLink] = useState('');
  const [currentImage, setCurrentImage] = useState('');
  const isMobile = useIsMobile();
  const isXy = useXy();
  const isGroup = useGroup();
  const isCategoryPage = type === 'category';
  const catalogLanguage = useSelector(currentCatalogSelector);
  const catalogBaseUrl = useSelector(catalogBaseUrlSelector);

  const translate = useTranslationFunction();
  const imagePrefix = imageAlt(translate('web20_products_image_of'));
  const imageSuffix = getImageAltSuffix(translate('web20_products_imagealt_suffix'));
  const imageContent = `${imagePrefix}${name}${imageSuffix}`;

  let SubtitleTag = 'span';
  let NameTag = 'span';
  if (!isGroup) {
    if (isAccordion) {
      SubtitleTag = 'h4';
      NameTag = 'h5';
    } else {
      SubtitleTag = 'h3';
      NameTag = 'h4';
    }
  }

  useEffect(() => {
    const productLink = `${catalogBaseUrl}/${catalogLanguage}/${
      type === 'category' ? 'systems' : type
    }/${identifier}`;
    setProductLink(productLink);
  }, [catalogBaseUrl, catalogLanguage, type, identifier]);

  useEffect(() => {
    const productIsLoaded = () => product?.constructor === Object && product.gallery?.list;
    const imageList = productIsLoaded();
    const image = imageList && getPrimaryImage(imageList);

    if (image) {
      setCurrentImage(image?.medium);
    }

    if (isCategoryPage && product?.pictureObject) {
      const setImage = responsiveImage(
        isMobile,
        product?.pictureObjectMobile,
        product?.pictureObject,
      );
      setCurrentImage(setImage?.url);
    }
  }, [product, isMobile, isCategoryPage]);

  const filterProductThumbnails = (imageContent, description) =>
    sortProductImages(product?.gallery)
      .slice(0, 5)
      .map((imageItem) => (
        <ImageThumbnail
          key={key(imageItem)}
          className="product-tile"
          thumbnail={imageItem.thumbnail}
          content={imageContent}
          description={description}
          onChange={() => setCurrentImage(imageItem?.medium)}
        />
      ));

  return (
    <div
      className={classNameBuilder(
        styles.tileWrapper,
        backgroundColor === 'white' && styles.whiteBackground,
        type === 'series' && styles.isSeries,
        isAccordion && styles.isAccordion,
      )}
    >
      <a href={productLink} target="_blank">
        <div className={styles.image}>
          <Image src={currentImage || pictureObject?.url} alt={imageContent} title={imageContent} />
        </div>
        {!isCategoryPage && (
          <div className={styles.thumbnails}>
            {filterProductThumbnails(imageContent, description)}
          </div>
        )}
        <div className={styles.content}>
          {createElement(
            SubtitleTag,
            {
              className: classNameBuilder(
                styles.subtitle,
                !isGroup && 'gdds-headline',
                isXy && styles['xy-subtitle'],
              ),
            },
            subtitle,
          )}

          {!isCategoryPage && state?.indicator && (
            <div className={styles.label}>
              <Translation id="web20_new_label" />
            </div>
          )}

          {createElement(
            NameTag,
            {
              className: classNameBuilder(
                styles.name,
                !isGroup && 'gdds-headline',
                isXy && styles['xy-name'],
              ),
            },
            name,
          )}

          {total > 0 && (
            <span className={classNameBuilder(styles.amount, isXy && styles['xy-text'])}>
              {total} &nbsp;
              <Translation id="web20_products" />
            </span>
          )}
        </div>
      </a>
    </div>
  );
};

export default OkvTile;
